import {
  Currency,
  ImpactShopProductQuickSelectOption,
  ImpactShopProductType,
  impactShopProductUnitTypePluralMapper,
  ImpactType,
  penceToDisplayPrice,
  round,
} from "@ecologi/shared";
import styled from "@emotion/styled";
import { useState } from "react";
import { Accordion, AccordionItem } from "../Accordion";
import { Button } from "../Button";
import { Heading } from "../Heading";
import { TextField, TextFieldChangeEvent } from "../TextField";

const Outer = styled.div`
  background-color: ${({ theme }) => theme.colors.snowdrop};
  border-left: 1px solid ${({ theme }) => theme.colors.mistLight};

  @media ${({ theme }) => theme.bp.desktopUp} {
    grid-row-start: span 100;
  }
`;

const AddToBasket = styled(Button)`
  margin-bottom: 48px;
`;

const Container = styled.div`
  position: sticky;
  top: 80px;
  background-color: ${({ theme }) => theme.colors.snowdrop};
  padding: 34px 24px 48px 24px;

  @media ${({ theme }) => theme.bp.desktopUp} {
    padding: 48px 24px;
  }

  @media ${({ theme }) => theme.bp.wideDesktopUp} {
    padding: 48px 40px;
  }
`;

const FundedBy = styled.div`
  margin-bottom: 24px;
`;

const ManualQuantity = styled.div`
  margin-bottom: 24px;
`;

const QuickSelect = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 32px;
`;

const QuickSelectOption = styled.button<{
  active: boolean;
}>`
  all: unset;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background-color: ${({ theme }) => theme.colors.mist};
  padding: 24px 12px;
  text-align: center;
  cursor: pointer;
  flex: 1;
  border: 2px solid
    ${(props) =>
      props.active ? props.theme.colors.forest : props.theme.colors.mist};
  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
  &:focus-visible {
    background: ${({ theme }) => theme.colors.mistDark};
    border-color: ${(props) =>
      props.active ? props.theme.colors.forest : props.theme.colors.mistDark};
  }
  &:hover:enabled {
    background-color: ${(props) =>
      props.active ? props.theme.colors.mist : props.theme.colors.mistDark};
    border: 2px solid
      ${(props) =>
        props.active ? props.theme.colors.forest : props.theme.colors.mistDark};
  }
`;

const QuickSelectOptionLabel = styled(Heading)`
  font-size: 16px !important;
  margin-bottom: 20px;
`;

const QuickSelectOptionPrice = styled.div`
  font-size: 14px;
`;

const Title = styled(Heading)`
  margin-bottom: 20px;
`;

const UnitPrice = styled.p`
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 24px;
  color: ${({ theme }) => theme.colors.charcoalLight};
`;

export type ImpactShopProductPageSidebarProps = {
  currency: Currency;
  onAddToBasket: (
    product: ImpactShopProductType,
    quantity: number,
    fundedBy: string
  ) => void;
  product: ImpactShopProductType;
};

export const ImpactShopProductPageSidebar = ({
  currency,
  onAddToBasket,
  product,
}: ImpactShopProductPageSidebarProps) => {
  const [activeQuickSelect, setActiveQuickSelect] = useState<number | null>(
    null
  );

  const accordionItems: AccordionItem[] = [
    {
      heading: "Description",
      description: product.display.description,
    },
    ...(product.display.impact
      ? [
          {
            heading: "Impact",
            description: product.display.impact,
          },
        ]
      : []),
    ...(product.display.standards
      ? [
          {
            heading: "Certifications",
            description: `This product is verified by one or more of the following standards: ${product.display.standards!.join(
              ", "
            )}.`,
          },
        ]
      : []),
  ];
  const [fundedBy, setFundedBy] = useState("");
  const [quantity, setQuantity] = useState<number | undefined>(undefined);

  const unitPrice = product.prices.find(
    (price) => price.currency.toLowerCase() === currency.toLowerCase()
  )!.amount;

  const handleAddToBasket = () => {
    onAddToBasket(product, quantity ?? 0, fundedBy);
  };

  const handleManualQuantityChange = (
    { target: { value } }: TextFieldChangeEvent,
    impactType: ImpactType
  ) => {
    const quantity =
      impactType === ImpactType.CARBON_OFFSETS
        ? Number(value)
        : round(Number(value), 0);

    setQuantity(quantity);
    setActiveQuickSelect(null);
  };

  const handleQuickSelect = (
    option: ImpactShopProductQuickSelectOption,
    i: number
  ) => {
    setQuantity(option.quantity);
    setActiveQuickSelect(i);
  };

  const hasQuickSelectOptions = product.quickSelectOptions.length > 0;

  return (
    <Outer>
      <Container>
        <Title as="h1" size={5}>
          {product.display.title}
        </Title>
        <UnitPrice>
          {penceToDisplayPrice(unitPrice, currency)}/{product.display.unitType}
        </UnitPrice>
        <QuickSelect>
          {product.quickSelectOptions.map((option, i) => (
            <QuickSelectOption
              disabled={product.isOutOfStock}
              active={activeQuickSelect === i}
              key={i}
              onClick={() =>
                !product.isOutOfStock && handleQuickSelect(option, i)
              }
            >
              <QuickSelectOptionLabel as="h3">
                {option.label}
              </QuickSelectOptionLabel>
              <QuickSelectOptionPrice>
                {penceToDisplayPrice(
                  option.quantity * unitPrice,
                  currency
                ).replace(".00", "")}
              </QuickSelectOptionPrice>
            </QuickSelectOption>
          ))}
        </QuickSelect>
        <ManualQuantity>
          <TextField
            label={`${
              hasQuickSelectOptions ? "Or enter" : "Enter"
            } a custom amount`}
            min={product.minQuantity}
            max={product.maxQuantity}
            isDisabled={product.isOutOfStock}
            name="manualQuantity"
            onChange={(e) => handleManualQuantityChange(e, product.impactType)}
            placeholder={`Minimum ${product.minQuantity} ${
              product.display.unitType
                ? product.minQuantity > 1
                  ? impactShopProductUnitTypePluralMapper[
                      product.display.unitType
                    ]
                  : product.display.unitType
                : ""
            }`}
            step={1}
            type="number"
            value={quantity === 0 ? "" : quantity}
          />
        </ManualQuantity>
        <FundedBy>
          <TextField
            helpText="This name will be displayed on your impact tile. You can use this to share your impact with your network."
            label="Funded by"
            isDisabled={product.isOutOfStock}
            name="fundedBy"
            placeholder="Enter an optional display name"
            onChange={(e) => setFundedBy(e.target.value)}
            value={fundedBy}
          />
        </FundedBy>
        <AddToBasket
          text={product.isOutOfStock ? "Out of stock" : "Add to basket"}
          primary
          disabled={
            product.isOutOfStock ||
            !quantity ||
            quantity < product.minQuantity ||
            quantity > product.maxQuantity
          }
          fullWidth
          onClick={handleAddToBasket}
        />
        <Accordion items={accordionItems} />
      </Container>
    </Outer>
  );
};
